import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  type KeyboardEvent,
} from 'react';
import { Send, MessageSquare } from 'lucide-react';
import type {
  ProMessage,
  ProMessageThreadForPro,
} from '../lib/api';

interface MessagesChatPanelProps {
  thread: ProMessageThreadForPro | null;
  messages: ProMessage[];
  isLoading: boolean;
  isError: boolean;
  isSending: boolean;
  onSend: (text: string) => void;
}

const MSG_MAX_LEN = 2000;

function formatTime(iso: string): string {
  const normalized = iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z';
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

function formatDayLabel(iso: string): string {
  const normalized = iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z';
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return iso;
  const today = new Date();
  const yesterday = new Date(Date.now() - 86400000);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, today)) return 'Oggi';
  if (sameDay(d, yesterday)) return 'Ieri';
  return d.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'long',
    year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
}

function parseIso(iso: string): number {
  const normalized = iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z';
  return new Date(normalized).getTime();
}

interface MessageGroup {
  kind: 'day' | 'msg';
  key: string;
  msg?: ProMessage;
  dayLabel?: string;
  showTime?: boolean;
}

/**
 * Trasforma una lista DESC (newest first) in una struttura ASC (oldest first)
 * con day separators e raggruppamento timestamp ogni 5 minuti / cambio sender.
 */
function buildGroups(messages: ProMessage[]): MessageGroup[] {
  const asc = [...messages].sort((a, b) => parseIso(a.created_at) - parseIso(b.created_at));
  const groups: MessageGroup[] = [];
  let lastDay: string | null = null;
  let lastTimestamp = 0;
  let lastFromRole: ProMessage['from_role'] | null = null;

  for (const msg of asc) {
    const dayLabel = formatDayLabel(msg.created_at);
    if (dayLabel !== lastDay) {
      groups.push({ kind: 'day', key: `d-${dayLabel}-${msg.id}`, dayLabel });
      lastDay = dayLabel;
      lastTimestamp = 0; // reset so first msg in a new day always shows time
      lastFromRole = null;
    }
    const ts = parseIso(msg.created_at);
    const showTime =
      ts - lastTimestamp > 5 * 60 * 1000 || msg.from_role !== lastFromRole;
    groups.push({ kind: 'msg', key: msg.id, msg, showTime });
    lastTimestamp = ts;
    lastFromRole = msg.from_role;
  }
  return groups;
}

export function MessagesChatPanel({
  thread,
  messages,
  isLoading,
  isError,
  isSending,
  onSend,
}: MessagesChatPanelProps) {
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastMsgIdRef = useRef<string | null>(null);

  const groups = useMemo(() => buildGroups(messages), [messages]);
  const lastMsgId = messages.length > 0 ? messages[0].id : null;

  // Auto-scroll bottom quando il thread cambia o arriva un nuovo messaggio.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (lastMsgIdRef.current !== lastMsgId) {
      el.scrollTop = el.scrollHeight;
      lastMsgIdRef.current = lastMsgId;
    }
  }, [lastMsgId, thread?.patient_id]);

  // Reset draft on thread change.
  useEffect(() => {
    setDraft('');
  }, [thread?.patient_id]);

  const submit = useCallback(() => {
    const trimmed = draft.trim();
    if (!trimmed || isSending || trimmed.length > MSG_MAX_LEN) return;
    onSend(trimmed);
    setDraft('');
  }, [draft, isSending, onSend]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const charCount = draft.length;
  const tooLong = charCount > MSG_MAX_LEN;
  const canSend = draft.trim().length > 0 && !isSending && !tooLong;

  if (!thread) {
    return (
      <section
        aria-label="Pannello chat"
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg)',
          color: 'var(--mute)',
          padding: 40,
        }}
      >
        <div style={{ maxWidth: 380, textAlign: 'center' }}>
          <MessageSquare size={48} strokeWidth={1.3} style={{ color: 'var(--mute-2)' }} aria-hidden />
          <h3
            style={{
              fontFamily: 'var(--display)',
              fontWeight: 800,
              fontSize: 22,
              letterSpacing: -0.4,
              color: 'var(--ink)',
              margin: '14px 0 6px',
            }}
          >
            Seleziona una conversazione
          </h3>
          <p style={{ fontSize: 13.5, lineHeight: 1.55, margin: 0, color: 'var(--mute)' }}>
            Apri un thread dalla sidebar per leggere i messaggi del paziente e rispondere.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label={`Chat con ${thread.patient_name}`}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg)',
        minWidth: 0,
        height: '100%',
      }}
    >
      <header
        style={{
          padding: '18px 24px',
          borderBottom: '1px solid var(--line)',
          background: 'var(--surface)',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <div
          aria-hidden
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            background: 'var(--ink)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 14,
            letterSpacing: 0.5,
          }}
        >
          {thread.patient_name
            .trim()
            .split(/\s+/)
            .map((p) => p[0])
            .slice(0, 2)
            .join('')
            .toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontFamily: 'var(--display)',
                fontWeight: 800,
                fontSize: 20,
                letterSpacing: -0.4,
                color: 'var(--ink)',
                lineHeight: 1.1,
              }}
            >
              {thread.patient_name}
            </h3>
            {thread.patient_human_id && (
              <span
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--mute)',
                  background: 'var(--line-3)',
                  padding: '2px 7px',
                  borderRadius: 5,
                  letterSpacing: 0.5,
                }}
              >
                {thread.patient_human_id}
              </span>
            )}
          </div>
          <div
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10.5,
              color: 'var(--mute)',
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              marginTop: 3,
            }}
          >
            Conversazione attiva
          </div>
        </div>
      </header>

      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {isError && (
          <div
            role="alert"
            style={{
              padding: 14,
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 10,
              color: 'var(--red)',
              fontSize: 13,
            }}
          >
            Errore caricamento messaggi. Riprova.
          </div>
        )}
        {!isError && isLoading && messages.length === 0 && (
          <div style={{ color: 'var(--mute)', fontSize: 13, padding: 20 }}>
            Caricamento messaggi…
          </div>
        )}
        {!isError && !isLoading && messages.length === 0 && (
          <div
            style={{
              color: 'var(--mute)',
              fontSize: 13,
              padding: '24px 12px',
              textAlign: 'center',
            }}
          >
            Nessun messaggio. Scrivi il primo per iniziare la conversazione.
          </div>
        )}
        {groups.map((g) => {
          if (g.kind === 'day') {
            return (
              <div
                key={g.key}
                style={{
                  alignSelf: 'center',
                  fontFamily: 'var(--mono)',
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 1.3,
                  color: 'var(--mute)',
                  textTransform: 'uppercase',
                  margin: '12px 0 6px',
                }}
              >
                — {g.dayLabel} —
              </div>
            );
          }
          const m = g.msg!;
          const isPro = m.from_role === 'pro';
          return (
            <div
              key={g.key}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isPro ? 'flex-end' : 'flex-start',
              }}
            >
              {g.showTime && (
                <div
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 10,
                    color: 'var(--mute)',
                    margin: '6px 4px 2px',
                    letterSpacing: 0.3,
                  }}
                >
                  {formatTime(m.created_at)}
                </div>
              )}
              <div
                style={{
                  maxWidth: '72%',
                  padding: '10px 14px',
                  borderRadius: isPro ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  background: isPro ? 'var(--blue)' : 'var(--surface)',
                  color: isPro ? '#fff' : 'var(--ink)',
                  border: isPro ? 'none' : '1px solid var(--line)',
                  fontSize: 14,
                  lineHeight: 1.4,
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  boxShadow: isPro ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
                }}
              >
                {/* Render text content as plain text (React auto-escapes via textContent). No HTML injection. */}
                {m.text}
              </div>
            </div>
          );
        })}
      </div>

      <footer
        style={{
          padding: '14px 24px 18px',
          borderTop: '1px solid var(--line)',
          background: 'var(--surface)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 10,
            border: tooLong ? '1px solid var(--red)' : '1px solid var(--line)',
            borderRadius: 12,
            padding: 8,
            background: 'var(--bg)',
          }}
        >
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={isSending}
            rows={1}
            placeholder="Scrivi un messaggio... (Enter = invia, Shift+Enter = a capo)"
            aria-label="Messaggio da inviare"
            maxLength={MSG_MAX_LEN * 2 /* hard ceiling, soft limit enforced visually */}
            style={{
              flex: 1,
              minHeight: 24,
              maxHeight: 140,
              padding: '8px 10px',
              border: 'none',
              background: 'transparent',
              fontFamily: 'var(--sans)',
              fontSize: 14,
              lineHeight: 1.4,
              resize: 'none',
              outline: 'none',
              color: 'var(--ink)',
            }}
          />
          <button
            type="button"
            onClick={submit}
            disabled={!canSend}
            aria-label="Invia messaggio"
            style={{
              flex: '0 0 auto',
              width: 38,
              height: 38,
              borderRadius: 10,
              border: 'none',
              background: canSend ? 'var(--green)' : 'var(--mute-3)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: canSend ? 'pointer' : 'not-allowed',
              transition: 'background 0.15s',
            }}
          >
            <Send size={16} strokeWidth={2} />
          </button>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 6,
            fontSize: 11,
            color: tooLong ? 'var(--red)' : 'var(--mute)',
          }}
        >
          <span style={{ letterSpacing: 0.2 }}>
            {isSending ? 'Invio…' : 'Enter per inviare · Shift+Enter per andare a capo'}
          </span>
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontVariantNumeric: 'tabular-nums',
              fontWeight: tooLong ? 700 : 500,
            }}
          >
            {charCount} / {MSG_MAX_LEN}
          </span>
        </div>
      </footer>
    </section>
  );
}
